import { DateTime } from "luxon";

import { CALL_STATUS, CALL_TYPES } from "@/constants/call";
import type { ModuleType } from "@/types/zoho.types";

export type CallDirection = (typeof CALL_TYPES)[keyof typeof CALL_TYPES];

export type CallStatus = (typeof CALL_STATUS)[keyof typeof CALL_STATUS];

export type CallStatusLog = {
    status: CallStatus;
    timestamp: string;
};

export enum CallFailureType {
    BUSY = "busy",
    NO_ANSWER = "no_answer",
    REJECTED = "rejected",
    TIMEOUT = "timeout",
    NETWORK_ERROR = "network_error",
    INVALID_NUMBER = "invalid_number",
    AUDIO_DEVICE = "audio_device",
    GENERIC = "generic",
}

export enum CallAnsweredByType {
    HUMAN = "human",
    MACHINE_START = "machine_start",
    FAX = "fax",
    UNKNOWN = "unknown",
}

export type Call = {
    id: string;
    userId: string;
    orgId: string;
    provider: string;
    callSid: string | null;
    callStartTime?: string;
    callEndTime?: string;
    callerNumber: string | null;
    callerName: string;
    calledNumber: string;
    calledName: string;
    calledEmail: string | null;
    leadId: string;
    moduleType?: ModuleType;
    recordId?: string;
    appointmentId?: string;
    callPurpose: string;
    callDuration: number | null;
    callAttempts: number;
    callResult: string | null;
    callRecordingUrl: string | null;
    answeredBy?: CallAnsweredByType | null;
    callNotes: string | null;
    callTranscript: string | null;
    statusLogs: CallStatusLog[];
    status: string;
    createdAt: string;
    updatedAt: string;
};

export type CallPayload = {
    To: string;
    From?: string;
    callId: string;
    userId: string;
    orgId: string;
    moduleType?: ModuleType;
    recordId?: string;
    appointmentId?: string;
    callPurpose?: string;
    calledName?: string;
    calledEmail?: string;
};

export type CallsResponse = {
    calls: Call[];
    total: number;
    page: number;
    limit: number;
};

export type KpiData = {
    all: number;
    bruttos: number;
    netto: number;
    averageCallDuration: number;
    inCallTime: number;
    callsPerHour: Record<string, number>;
    inCallTimePerHour?: Record<string, number>;
};

export type TeamMemberKpiData = {
    userId: string;
    userEmail: string;
    kpiData: KpiData;
};

export type TeamMembersKpiResponse = {
    teamId: string;
    teamName: string;
    members: TeamMemberKpiData[];
};

export enum AppointmentNotificationType {
    STATUS_UPDATE = "APPOINTMENT_STATUS_UPDATE",
    REMOVAL = "REMOVE_APPOINTMENT",
}

export type AppointmentStatusUpdatePayload = {
    type: AppointmentNotificationType.STATUS_UPDATE;
    appointmentId: string;
    brandName: string;
    leadId?: string;
};

export type AppointmentRemovalPayload = {
    type: AppointmentNotificationType.REMOVAL;
    appointmentId: string;
    brandName: string;
};

export type AppointmentNotificationPayload = AppointmentStatusUpdatePayload | AppointmentRemovalPayload;

type AppointmentNotificationBase = {
    id: string;
    appointmentId: string;
    brandName: string;
    receivedAt: DateTime;
    read?: boolean;
};

export type AppointmentStatusUpdateNotification = AppointmentNotificationBase & {
    type: AppointmentNotificationType.STATUS_UPDATE;
    leadId?: string;
};

export type AppointmentRemovalNotification = AppointmentNotificationBase & {
    type: AppointmentNotificationType.REMOVAL;
};

export type AppointmentNotification = AppointmentStatusUpdateNotification | AppointmentRemovalNotification;
